import React, { useMemo } from 'react';
import { COUNTRIES, COUNTRY_LIST } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { groupAreasByCountry } from '../../utils/areaCountryGroups';

interface CountryFilterProps {
  /** Every area the picker holds; the chips come from these (one per country that has any, with its count). */
  areas: ReadonlyArray<{ type: string; country_code?: string | null }>;
  /** The chosen country, or null for All. */
  value: CountryCode | null;
  onChange(country: CountryCode | null): void;
  className?: string;
}

const chip = (active: boolean) =>
  `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
    active
      ? 'border-brand-500 bg-brand-500 text-on-brand'
      : 'border-paper-300 bg-surface text-ink-muted hover:bg-paper-100'
  }`;

const count = (active: boolean) =>
  `rounded-full px-1.5 text-xs font-mono ${active ? 'bg-on-brand/20' : 'bg-paper-200 text-ink-subtle'}`;

/**
 * "All" and one chip per country the person has areas in, each with its count. The countries come from the
 * registry (name and flag), in registry order. A country asked for in the URL that has no areas still gets a
 * chip, so the filter on screen is always visible and can be turned off.
 */
const CountryFilter: React.FC<CountryFilterProps> = ({ areas, value, onChange, className = '' }) => {
  const chips = useMemo(() => {
    const groups = groupAreasByCountry([...areas]);
    const counts = new Map<CountryCode, number>();
    for (const g of groups) if (g.country) counts.set(g.country, g.areas.length);
    if (value && !counts.has(value)) counts.set(value, 0);
    return COUNTRY_LIST.filter((c) => counts.has(c.code)).map((c) => ({ code: c.code, n: counts.get(c.code) ?? 0 }));
  }, [areas, value]);

  // A person with areas in one country (or none) has nothing to filter by.
  if (chips.length < 2 && value === null) return null;

  const all = areas.length;
  return (
    <div role="group" aria-label="Filter by country" className={`flex flex-wrap items-center justify-center gap-2 ${className}`}>
      <button type="button" aria-label={`All ${all}`} aria-pressed={value === null} className={chip(value === null)} onClick={() => onChange(null)}>
        <span>All</span>
        <span className={count(value === null)}>{all}</span>
      </button>
      {chips.map(({ code, n }) => {
        const active = value === code;
        return (
          <button key={code} type="button" aria-label={`${COUNTRIES[code].label} ${n}`} aria-pressed={active} className={chip(active)} onClick={() => onChange(code)}>
            <span aria-hidden="true">{COUNTRIES[code].flag}</span>
            <span>{COUNTRIES[code].label}</span>
            <span className={count(active)}>{n}</span>
          </button>
        );
      })}
    </div>
  );
};

export default CountryFilter;
